/**
 * ScoreProfileCard — radar chart comparing the top 5 sites across criteria.
 * Split out of the original VisualizationPanel so it can occupy its own grid
 * cell in the redesigned two-row Scoring layout.
 *
 * Returns null when there's nothing to chart (no sites or no scored sites),
 * which lets the parent grid collapse the cell and expand RankingCard.
 */

import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Activity } from 'lucide-react'

import ScoreProfileChart from './ScoreProfileChart'

interface SiteSummary {
	siteId: string
	siteName: string
	overallScore: number | null
	criteria: Array<{ criterionId: string; name: string; weight: number; avgScore: number | null }>
}

interface ScoreProfileCardProps {
	evalId: string
}

const ScoreProfileCard = ({ evalId }: ScoreProfileCardProps) => {
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const sites: SiteSummary[] = summary?.sites ?? []

	const hasAnyScores = useMemo(() => sites.some(s => s.overallScore != null), [sites])

	// Cap at 5 sites in the radar to keep it readable; the in-page Comparison
	// view (F-phase) handles many-site cases.
	const radarData = useMemo(() => {
		if (sites.length === 0) return null
		return {
			criteria: sites[0].criteria.map(c => c.name),
			sites: sites.slice(0, 5).map(site => ({ name: site.siteName, values: site.criteria.map(c => c.avgScore) })),
		}
	}, [sites])

	if (!hasAnyScores || !radarData) return null

	return (
		<Card>
			<CardHeader className="pb-3">
				<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
					<Activity className="w-4 h-4" />
					Score profile
				</h4>
				<p className="text-xs text-gray-500 mt-1">Top 5 sites · 1–10 per criterion</p>
			</CardHeader>
			<CardContent>
				<ScoreProfileChart criteria={radarData.criteria} sites={radarData.sites} />
			</CardContent>
		</Card>
	)
}

export default ScoreProfileCard

/**
 * Helper for parents that need to know whether the card will render without
 * mounting it. Currently unused: the three-column ScoringWorkspace stacks
 * RankingCard and ScoreProfileCard in one column, so there is no sibling width
 * to reallocate and the card's own `null` return is enough. Kept because it is
 * the supported way to reserve grid space for the radar if the layout goes
 * back to side-by-side cards.
 */
export function useHasScoreProfile(evalId: string): boolean {
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const sites: SiteSummary[] = summary?.sites ?? []
	return useMemo(() => sites.length > 0 && sites.some(s => s.overallScore != null), [sites])
}
